import { Typography, useTheme } from '@material-ui/core'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'

import { PromotionStatsRow } from '../../graphql/promotions'
import { formatCount } from '../../helpers/promotionAmount'
import { makeTruncatedCategoryTick } from '../../helpers/rechartsAxisTick'
import { getDirection, getTranslate } from '../../localization'
import {
  chartedCurrencies,
  discountSeries,
  discountTooltipText,
  hasInFlight,
  hasSomethingToShow,
  usageSeries
} from './chartSeries'
import useStyles from './styles/charts.styles'

/**
 * The Overview's charts.
 *
 * TWO RULES SHAPE EVERYTHING HERE, and both come from the data rather than from taste.
 *
 * 1. COUNTS MAY BE PLOTTED TOGETHER; MONEY MAY NOT. `formatCount`'s own docstring says why:
 *    counts are dimensionless, money is per-currency and minor-unit scale differs between
 *    currencies. A bar of 1,000,000 IRR beside one of 5.00 USD invites the reader to compare
 *    bar lengths that mean different things — the defect this codebase refuses everywhere
 *    else, drawn as a picture. So the count chart spans every promotion, and money is charted
 *    ONE CHART PER CURRENCY with the currency in its own heading.
 *
 * 2. EVERY NUMBER GOES THROUGH THE TABLE'S OWN FORMATTERS. Recharts generates axis ticks and
 *    tooltip values itself, in Latin digits, with no knowledge of the locale. Left alone it
 *    would put Latin numerals on a Persian screen beside a table showing Persian ones — the
 *    mixed-numeral inconsistency already reported on this screen. Every tick and every
 *    tooltip is routed through formatCount / formatMinorWithCurrency.
 *
 * ACCESSIBILITY: these are decorative, and marked so. Recharts renders SVG with no meaningful
 * structure for a screen reader, and the table below carries exactly the same figures with
 * proper semantics. `aria-hidden` says that honestly rather than dressing the SVG in ARIA
 * that would announce a shape instead of a number. The table is the accessible presentation;
 * the chart is a second view of it for people who read shapes faster than columns.
 */

interface Props {
  rows: PromotionStatsRow[]
}

const OverviewCharts = ({ rows }: Props) => {
  const classes = useStyles()
  const theme = useTheme()
  const isRtl = getDirection() === 'rtl'

  if (rows.length === 0) {
    // No empty-state illustration: the screen already says "no promotions matched" once,
    // above the table. Saying it twice reads as two separate failures.
    return null
  }

  // Derived by pure functions in ./chartSeries so the invariants they encode are testable:
  // nothing inside a Recharts chart reaches the DOM under jsdom, so a test written against
  // the rendered chart can see that one exists and nothing about what it holds.
  const byApplied = usageSeries(rows)
  // A legend key for a series that draws nothing is a label pointing at absent ink. On a
  // tenant with nothing reserved the grey "In flight" entry advertised a colour that never
  // appeared. The totals card still reports the zero — that one stays, because "how much is
  // reserved" has to remain findable and it carries the "not included in Applied" caveat.
  const anyInFlight = hasInFlight(byApplied)
  const currencies = chartedCurrencies(rows)

  // Both charts are suppressed together when nothing has been applied at all, rather than
  // drawing two empty axes under two headings. The totals panel above has already said zero,
  // and the table below lists every promotion — an empty chart adds a third telling of the
  // same nothing, and is the state this screen spent most of its time in.
  if (
    byApplied.length === 0 &&
    currencies.every((c) => !hasSomethingToShow(discountSeries(rows, c)))
  ) {
    return null
  }

  return (
    <div className={classes.root}>
      {byApplied.length > 0 && (
        <section aria-labelledby="promotion-usage-chart-heading">
          <Typography
            variant="h6"
            component="h2"
            id="promotion-usage-chart-heading"
            className={classes.heading}>
            {getTranslate('Applied and in flight — most applied first')}
          </Typography>
          <div
            className={classes.chart}
            aria-hidden="true"
            data-cy="OverviewUsageChart">
            <ResponsiveContainer
              width="100%"
              height={40 * byApplied.length + 60}>
              <BarChart data={byApplied} layout="vertical" barCategoryGap={8}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                {/* reversed in RTL so the bars grow from the right, the direction a Persian
                  reader's eye starts from. Recharts has no RTL mode; this is the axis-level
                  equivalent and the only part of the layout that needs mirroring. */}
                <XAxis
                  type="number"
                  reversed={isRtl}
                  tickFormatter={(value: number) => formatCount(value)}
                  allowDecimals={false}
                  // Ends at the data, not at the next round tick. Recharts pads its auto-domain,
                  // which drew an axis to 4 for a maximum of 2 — and dropping the blank rows made
                  // that worse, not better: the bars are now short against a padded axis instead
                  // of lost among empty ones.
                  domain={[0, 'dataMax']}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={180}
                  orientation={isRtl ? 'right' : 'left'}
                  tick={makeTruncatedCategoryTick(180)}
                />
                <Tooltip
                  formatter={(value: number) => formatCount(value)}
                  contentStyle={{ direction: isRtl ? 'rtl' : 'ltr' }}
                />
                {anyInFlight && <Legend />}
                <Bar
                  dataKey="applied"
                  name={getTranslate('Applied')}
                  fill={theme.palette.primary.main}
                  radius={[0, 3, 3, 0]}
                />
                {anyInFlight && (
                  <Bar
                    dataKey="inFlight"
                    name={getTranslate('In flight')}
                    fill={theme.palette.grey[400]}
                    radius={[0, 3, 3, 0]}
                  />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {currencies.map((currency) => {
        const forCurrency = discountSeries(rows, currency)

        if (!hasSomethingToShow(forCurrency)) {
          return null
        }

        return (
          <section
            key={currency}
            aria-labelledby={`promotion-discount-chart-${currency}`}>
            <Typography
              variant="h6"
              component="h2"
              id={`promotion-discount-chart-${currency}`}
              className={classes.heading}>
              {/* The currency is IN THE HEADING, not only on the axis. It is what makes this
                  chart's numbers meaningful, and a reader who skims headings must not be able
                  to mistake one currency's chart for another's. */}
              {/* Each chart states its own ordering, because they no longer hold the same
                  promotions: each drops its own zeros, so one applied 3 times with no discount
                  appears here and not there. Two identical-looking charts sharing a category
                  axis of names otherwise invite reading row 1 of one as row 1 of the other. */}
              {getTranslate('Discount given ({currency}) — largest first', {
                currency
              })}
            </Typography>
            <div
              className={classes.chart}
              aria-hidden="true"
              data-cy={`OverviewDiscountChart-${currency}`}>
              <ResponsiveContainer
                width="100%"
                height={40 * forCurrency.length + 60}>
                <BarChart
                  data={forCurrency}
                  layout="vertical"
                  barCategoryGap={8}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    reversed={isRtl}
                    tickFormatter={(value: number) => formatCount(value)}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={180}
                    orientation={isRtl ? 'right' : 'left'}
                    tick={makeTruncatedCategoryTick(180)}
                  />
                  <Tooltip
                    formatter={(_value: number, _name: string, entry: any) =>
                      discountTooltipText(entry?.payload, currency)
                    }
                    contentStyle={{ direction: isRtl ? 'rtl' : 'ltr' }}
                  />
                  <Bar
                    dataKey="discount"
                    name={getTranslate('Discount given ({currency})', {
                      currency
                    })}
                    fill={theme.palette.primary.main}
                    radius={[0, 3, 3, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>
        )
      })}
    </div>
  )
}

export default OverviewCharts
